import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { ExternalLink, MapPin, Phone, X } from 'lucide-react'
import { GuestLayout } from '@/layouts/GuestLayout'
import type { GuestMapCenter, GuestProperty, MapPOI } from '@/lib/types'

interface Props {
  property: GuestProperty
  pois: MapPOI[]
  center: GuestMapCenter | null
}

const DEFAULT_CENTER: [number, number] = [-9.1393, 38.7223] // Lisbon

const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
}

function shareTokenFromUrl(): string {
  const match = window.location.pathname.match(/\/g\/([^/]+)/)
  return match ? match[1] : ''
}

function trackPoiView(poiId: number) {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  const shareToken = shareTokenFromUrl()
  fetch(`/g/${shareToken}/engagement_events`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token ?? '',
    },
    body: JSON.stringify({ event_type: 'poi_view', poi_id: poiId }),
  }).catch(() => {})
}

interface POIDetailProps {
  poi: MapPOI
  onClose: () => void
}

function POIDetail({ poi, property, shareToken, onClose }: POIDetailProps & { property: GuestProperty; shareToken: string }) {
  useEffect(() => {
    trackPoiView(poi.id)
  }, [poi.id])

  return (
    <div
      className="absolute left-4 right-4 z-10 g-tap"
      style={{
        bottom: 'calc(90px + env(safe-area-inset-bottom))',
        background: 'var(--g-surface, #fff)',
        borderRadius: 18, padding: 14,
        border: '1px solid var(--g-line)',
        boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
        display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer',
      }}
      onClick={() => window.location.href = `/g/${shareToken}/poi/${poi.id}`}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="g-mono" style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 3 }}>
          {poi.category ?? 'Place'}
        </div>
        <div className="g-serif" style={{ fontSize: 19, lineHeight: 1.05, color: 'var(--ink-900)', letterSpacing: '-0.01em', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {poi.name}
        </div>
        {poi.description && (
          <div style={{ fontSize: 12, color: 'var(--ink-500)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {poi.description.slice(0, 70)}…
          </div>
        )}
      </div>
      <button
        onClick={e => { e.stopPropagation(); onClose() }}
        aria-label="Close"
        style={{ background: 'var(--sand-100, #F4ECE0)', border: 'none', borderRadius: 999, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
      >
        <X size={14} style={{ color: 'var(--ink-700)' }} />
      </button>
    </div>
  )
}

export default function Map({ property, pois, center }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef       = useRef<maplibregl.Map | null>(null)
  const [selectedPoi, setSelectedPoi] = useState<MapPOI | null>(null)
  const [mapError, setMapError]       = useState(false)
  const shareToken = shareTokenFromUrl()

  useEffect(() => {
    if (!containerRef.current) return

    const initialCenter: [number, number] = center
      ? [center.lng, center.lat]
      : DEFAULT_CENTER

    let map: maplibregl.Map
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: OSM_STYLE,
        center: initialCenter,
        zoom: 14,
      })
    } catch {
      setMapError(true)
      return
    }

    map.on('load', () => {
      pois.forEach((poi) => {
        const marker = new maplibregl.Marker({ color: property.brand_color })
          .setLngLat([poi.lng, poi.lat])
          .addTo(map)

        marker.getElement().addEventListener('click', () => {
          setSelectedPoi(poi)
        })
      })

      if (pois.length > 0) {
        const bounds = new maplibregl.LngLatBounds()
        pois.forEach((p) => bounds.extend([p.lng, p.lat]))
        map.fitBounds(bounds, { padding: 60, maxZoom: 15 })
      }
    })

    map.on('error', () => setMapError(true))
    mapRef.current = map

    return () => {
      mapRef.current = null
      map.remove()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (mapError) {
    return (
      <GuestLayout brandColor={property.brand_color}>
        <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 14, color: 'var(--ink-500)', fontStyle: 'italic' }}>Map unavailable — browse places in the Guide tab.</p>
          {pois.map((poi) => (
            <div key={poi.id} style={{ background: 'var(--g-surface, #fff)', borderRadius: 14, border: '1px solid var(--g-line)', padding: 14 }}>
              <div className="g-serif" style={{ fontSize: 17, color: 'var(--ink-900)' }}>{poi.name}</div>
              {poi.category && <div className="g-mono" style={{ fontSize: 10, color: 'var(--ink-500)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>{poi.category}</div>}
            </div>
          ))}
          {pois.length === 0 && <p style={{ fontSize: 14, color: 'var(--ink-500)' }}>No locations available.</p>}
        </div>
      </GuestLayout>
    )
  }

  return (
    <GuestLayout brandColor={property.brand_color}>
      {/* Use h-dvh directly so the map fills the viewport regardless of pb-32 on <main> */}
      <div className="relative" style={{ height: '100dvh' }}>
        {/* Do NOT use absolute+inset-0 here — MapLibre's own CSS sets .maplibregl-map { position: relative }
            which overrides `position: absolute`, collapsing height to 0. Use w/h 100% instead. */}
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

        {selectedPoi && (
          <POIDetail
            poi={selectedPoi}
            property={property}
            shareToken={shareToken}
            onClose={() => setSelectedPoi(null)}
          />
        )}

        {pois.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p
              style={{
                fontSize: 14, color: 'var(--ink-500)',
                background: 'rgba(251, 247, 241, 0.9)',
                borderRadius: 10, padding: '8px 14px',
              }}
            >
              No locations available.
            </p>
          </div>
        )}
      </div>
    </GuestLayout>
  )
}
